import { useState, useEffect, useMemo, useCallback } from "react";
import { format, eachDayOfInterval, startOfMonth, endOfMonth } from "date-fns";
import { it } from "date-fns/locale";
import Select from "react-select";
// Generatore PDF compatibile React 19
import { downloadPresenzeDipendentePdf } from "../../stampe/generatori/presenzeDipendentePdf";
import MiniCalendario from "../../components/MiniCalendario";
import "./FoglioPresenza.css";

// Import hooks
import { useCollection } from "../../hooks/useCollection";
import { useAuthContext } from "../../hooks/useAuthContext";
import { useFirestore } from "../../hooks/useFirestore";
import { useDocument } from "../../hooks/useDocument";
import { database, timestamp } from "../../firebase/config";
import ValorizationModal from "./ValorizationModal";

function getDateFromValue(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === "function") {
    try {
      return value.toDate();
    } catch {
      return null;
    }
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatTimeValue(value) {
  const date = getDateFromValue(value);
  return date ? format(date, "HH:mm") : "";
}

function getTimbraturaReferenceDate(timbratura, sourceType) {
  if (sourceType === "timbrature_qr") {
    if (
      typeof timbratura.businessDate === "string" &&
      timbratura.businessDate
    ) {
      return timbratura.businessDate;
    }
    const openedAt = getDateFromValue(timbratura.openedAt);
    return openedAt ? format(openedAt, "yyyy-MM-dd") : "";
  }

  const registrationDate = getDateFromValue(timbratura.dataRegistrazione);
  return registrationDate ? format(registrationDate, "yyyy-MM-dd") : "";
}

function normalizeTimbratura(timbratura, sourceType) {
  const oraIngresso =
    sourceType === "timbrature_qr"
      ? timbratura.openedAt
      : timbratura.oraIngresso;
  const oraUscita =
    sourceType === "timbrature_qr" ? timbratura.closedAt : timbratura.oraUscita;
  const isAutoClosedBySystem =
    sourceType === "timbrature_qr" &&
    timbratura.autoClosed === true &&
    timbratura.closedBy === "system";
  const ingressoDate = getDateFromValue(oraIngresso);
  const uscitaDate = getDateFromValue(oraUscita);
  const ingressoLabel = ingressoDate ? format(ingressoDate, "HH:mm") : "--:--";
  const uscitaLabel = uscitaDate ? format(uscitaDate, "HH:mm") : "--:--";
  const isOvernight =
    ingressoDate &&
    uscitaDate &&
    format(ingressoDate, "yyyy-MM-dd") !== format(uscitaDate, "yyyy-MM-dd");

  return {
    id: timbratura.id,
    selectionKey: `${sourceType}:${timbratura.id}`,
    sourceType,
    sourceLabel:
      sourceType === "timbrature_qr"
        ? isAutoClosedBySystem
          ? "Timbratura QR (chiusa dal sistema)"
          : "Timbratura QR"
        : "Timbratura",
    dataRiferimento: getTimbraturaReferenceDate(timbratura, sourceType),
    oraIngresso,
    oraUscita,
    ingressoDate,
    uscitaDate,
    cantiere:
      timbratura.cantiere && typeof timbratura.cantiere === "object"
        ? timbratura.cantiere.nome
        : timbratura.cantiere || "",
    mezzo:
      timbratura.mezzo && typeof timbratura.mezzo === "object"
        ? timbratura.mezzo
        : timbratura.mezzo || "",
    note: timbratura.note || "",
    label: `${sourceType === "timbrature_qr" ? "QR" : "Classica"} • ${ingressoLabel} - ${uscitaLabel}${isOvernight ? " (+1 giorno)" : ""}${isAutoClosedBySystem ? " • chiusa dal sistema" : ""}`,
    raw: timbratura,
  };
}

function normalizeWorkDateRange(startValue, endValue) {
  const startDate = getDateFromValue(startValue);
  const endDate = getDateFromValue(endValue);

  if (!startDate || !endDate) {
    return { startDate, endDate };
  }

  const normalizedEndDate = new Date(endDate);
  if (normalizedEndDate < startDate) {
    normalizedEndDate.setDate(normalizedEndDate.getDate() + 1);
  }

  return {
    startDate,
    endDate: normalizedEndDate,
  };
}

export default function FoglioPresenza() {
  // PDF generator non dipende dalla versione di React
  const { user } = useAuthContext();
  const { document: userProfile } = useDocument("users", user?.uid);

  const [monthFilter, setMonthFilter] = useState(() => {
    // Set current month as default
    const now = new Date();
    return format(now, "yyyy-MM");
  });

  // Firestore hooks
  const monthRange = useMemo(() => {
    if (!monthFilter) return null;
    const [yearStr, monthStr] = monthFilter.split("-");
    const start = startOfMonth(
      new Date(Number(yearStr), Number(monthStr) - 1, 1),
    );
    const end = endOfMonth(start);
    return {
      startTs: timestamp.fromDate(start),
      endTs: timestamp.fromDate(end),
    };
  }, [monthFilter]);

  const presenzeQuery = useMemo(() => {
    if (!monthRange) return null;
    return [
      ["dataRegistrazione", ">=", monthRange.startTs],
      ["dataRegistrazione", "<=", monthRange.endTs],
    ];
  }, [monthRange]);

  const timbratureQuery = useMemo(() => {
    if (!monthRange) return null;
    return [
      ["dataRegistrazione", ">=", monthRange.startTs],
      ["dataRegistrazione", "<=", monthRange.endTs],
    ];
  }, [monthRange]);

  const timbratureQrQuery = useMemo(() => {
    if (!user?.uid) return null;
    return ["userId", "==", user.uid];
  }, [user?.uid]);

  const reperibilitaQuery = useMemo(() => {
    if (!user?.uid) return null;
    return ["userId", "==", user.uid];
  }, [user?.uid]);

  const { documents: presenzeData, error: presenzeError } = useCollection(
    presenzeQuery ? "presenze" : null,
    presenzeQuery,
    ["dataRegistrazione", "asc"],
    null,
    true,
  );
  const { documents: timbratureData, error: timbratureError } = useCollection(
    timbratureQuery ? "timbrature" : null,
    timbratureQuery,
    ["dataRegistrazione", "asc"],
    null,
    true,
  );
  const { documents: timbratureQrData, error: timbratureQrError } =
    useCollection(
      timbratureQrQuery ? "timbrature_qr" : null,
      timbratureQrQuery,
      null,
      null,
      true,
    );
  const { documents: projectsData } = useCollection(
    "projects",
    null,
    null,
    null,
    false,
  );
  const { documents: automezziData } = useCollection(
    "automezzi",
    null,
    null,
    null,
    false,
  );
  const { documents: reperibilita, error: reperibilityError } = useCollection(
    reperibilitaQuery ? "reperibilita" : null,
    reperibilitaQuery,
    null,
    null,
    true,
  );
  const { addDocument } = useFirestore("presenze");
  const { addDocument: addReperibilita, updateDocument: updateReperibilita } =
    useFirestore("reperibilita");

  // State management
  const [activeTab, setActiveTab] = useState("presenze");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showValorizationModal, setShowValorizationModal] = useState(false);
  const [documentiData, setDocumentiData] = useState(null);
  const [speseData, setSpeseData] = useState(null);
  const [formData, setFormData] = useState({
    dataRegistrazione: "",
    tipoPresenza: "lavorativa",
    oraIngresso: "",
    oraUscita: "",
    cantiere: "",
    mezzo: "",
    durataPausa: "1",
    isFerie: false,
    isMalattia: false,
    isRiposo: false,
    codiceMalattia: "",
    note: "",
  });

  // Portal target for react-select menus
  const [portalTarget, setPortalTarget] = useState(null);

  useEffect(() => {
    const div = document.createElement("div");
    div.className = "foglio-presenza-portal";
    document.body.appendChild(div);
    setPortalTarget(div);
    return () => {
      document.body.removeChild(div);
    };
  }, []);

  const [hasTimbratura, setHasTimbratura] = useState(false);
  const [relatedTimbratura, setRelatedTimbratura] = useState(null);
  const [availableTimbrature, setAvailableTimbrature] = useState([]);

  useEffect(() => {
    if (!showValorizationModal || !user?.uid) return;

    let isActive = true;

    const fetchExtra = async () => {
      try {
        const documentiSnap = await database
          .collection("documenti")
          .where("owner", "==", user.uid)
          .get();
        const documenti = documentiSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        const [speseByStringSnap, speseByObjectSnap] = await Promise.all([
          database.collection("spese").where("autore", "==", user.uid).get(),
          database.collection("spese").where("autore.id", "==", user.uid).get(),
        ]);

        const mergedSpeseById = new Map();
        speseByStringSnap.docs.forEach((d) => {
          mergedSpeseById.set(d.id, { id: d.id, ...d.data() });
        });
        speseByObjectSnap.docs.forEach((d) => {
          mergedSpeseById.set(d.id, { id: d.id, ...d.data() });
        });

        if (!isActive) return;
        setDocumentiData(documenti);
        setSpeseData(Array.from(mergedSpeseById.values()));
      } catch {
        if (!isActive) return;
        setDocumentiData([]);
        setSpeseData([]);
      }
    };

    fetchExtra();

    return () => {
      isActive = false;
    };
  }, [showValorizationModal, user?.uid]);

  // On-call (Reperibilità) state
  const [showReperibilityModal, setShowReperibilityModal] = useState(false);
  const [reperibilityFormData, setReperibilityFormData] = useState({
    dataInizio: "",
    dataFine: "",
    note: "",
  });

  // Malattia state
  const [showMalattiaModal, setShowMalattiaModal] = useState(false);
  const [malattiaFormData, setMalattiaFormData] = useState({
    dataInizio: "",
    dataFine: "",
    note: "",
  });

  // Details Modal State
  const [selectedReperibilita, setSelectedReperibilita] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsInterventi, setDetailsInterventi] = useState([]);

  // Prevent body scroll when any modal is open
  useEffect(() => {
    if (
      showAddModal ||
      showReperibilityModal ||
      showMalattiaModal ||
      showDetailsModal
    ) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [
    showAddModal,
    showReperibilityModal,
    showMalattiaModal,
    showDetailsModal,
  ]);

  const [currentIntervento, setCurrentIntervento] = useState({
    data: "",
    oraInizio: "",
    oraFine: "",
    luogo: "",
  });

  const handleInterventoChange = (e) => {
    const { name, value } = e.target;
    setCurrentIntervento((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOpenDetails = (rep) => {
    setSelectedReperibilita(rep);
    setDetailsInterventi(rep.interventi || []);
    setShowDetailsModal(true);
  };

  const addIntervento = () => {
    if (
      !currentIntervento.data ||
      !currentIntervento.oraInizio ||
      !currentIntervento.oraFine ||
      !currentIntervento.luogo
    ) {
      alert("Tutti i campi dell'intervento sono obbligatori");
      return;
    }

    setDetailsInterventi((prev) => [...prev, currentIntervento]);
    setCurrentIntervento({
      data: "",
      oraInizio: "",
      oraFine: "",
      luogo: "",
    });
  };

  const removeIntervento = (index) => {
    setDetailsInterventi((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveDetails = async () => {
    if (!selectedReperibilita) return;

    try {
      await updateReperibilita(selectedReperibilita.id, {
        interventi: detailsInterventi,
      });
      setShowDetailsModal(false);
      setSelectedReperibilita(null);
    } catch (error) {
      console.error("Errore salvataggio interventi:", error);
      alert("Errore durante il salvataggio degli interventi.");
    }
  };

  // Prepare options for React-Select
  const cantiereOptions = useMemo(() => {
    return (
      projectsData
        ?.filter((project) => !project.archived)
        .map((project) => ({
          value: project.nome,
          label: `${project.categoria} - ${project.nome}`,
        })) || []
    );
  }, [projectsData]);

  const mezzoOptions = useMemo(() => {
    return [
      { value: "nessuno", label: "Nessuno" },
      ...(automezziData?.map((automezzo) => ({
        value: automezzo,
        label: `${automezzo.modello} - ${automezzo.targa}`,
      })) || []),
    ];
  }, [automezziData]);

  const tipoPresenzaOptions = [
    { value: "lavorativa", label: "Lavorativa" },
    { value: "ferie", label: "Ferie" },
    { value: "riposo", label: "Riposo" },
  ];

  const userPresenze = useMemo(() => {
    const all = presenzeData || [];
    return all
      .filter((p) => p.userId === user?.uid)
      .sort((a, b) => {
        const dateA = a.dataRegistrazione?.toDate() || new Date(0);
        const dateB = b.dataRegistrazione?.toDate() || new Date(0);
        return dateA - dateB;
      });
  }, [presenzeData, user?.uid]);

  const userTimbrature = useMemo(() => {
    const all = timbratureData || [];
    return all
      .filter((t) => t.userId === user?.uid)
      .sort((a, b) => {
        const dateA = a.dataRegistrazione?.toDate() || new Date(0);
        const dateB = b.dataRegistrazione?.toDate() || new Date(0);
        return dateA - dateB;
      });
  }, [timbratureData, user?.uid]);

  const userTimbratureQr = useMemo(() => {
    const all = timbratureQrData || [];
    return all
      .filter((t) => t.userId === user?.uid)
      .filter((t) => {
        if (!monthFilter) return true;
        if (typeof t.businessDate === "string" && t.businessDate) {
          return t.businessDate.startsWith(monthFilter);
        }
        const openedAt = getDateFromValue(t.openedAt);
        return openedAt ? format(openedAt, "yyyy-MM") === monthFilter : false;
      })
      .sort((a, b) => {
        const dateA = a.openedAt?.toDate() || new Date(0);
        const dateB = b.openedAt?.toDate() || new Date(0);
        return dateA - dateB;
      });
  }, [timbratureQrData, user?.uid, monthFilter]);

  // Filter and sort on-call data to show only current user's records
  const userReperibilita = useMemo(() => {
    const reps = reperibilita || [];
    return reps
      .filter((rep) => {
        if (rep.userId !== user?.uid) return false;

        if (monthFilter) {
          const dataInizio = rep.dataInizio?.toDate();
          const dataFine = rep.dataFine?.toDate();
          if (dataInizio && dataFine) {
            const monthStart = new Date(monthFilter + "-01");
            const monthEnd = new Date(
              monthStart.getFullYear(),
              monthStart.getMonth() + 1,
              0,
            );

            if (dataFine < monthStart || dataInizio > monthEnd) return false;
          }
        }

        return true;
      })
      .sort((a, b) => {
        const dateA = a.dataInizio?.toDate() || new Date(0);
        const dateB = b.dataInizio?.toDate() || new Date(0);
        return dateA - dateB;
      });
  }, [reperibilita, user?.uid, monthFilter]);

  // Format time helper
  const formatTime = (timestamp) => {
    if (!timestamp) return "-";
    return format(timestamp.toDate(), "HH:mm", { locale: it });
  };

  const allUserTimbrature = useMemo(() => {
    return [...userTimbrature, ...userTimbratureQr]
      .map((timbratura) =>
        normalizeTimbratura(
          timbratura,
          timbratura.openedAt ? "timbrature_qr" : "timbrature",
        ),
      )
      .sort((a, b) => {
        const dateA = a.ingressoDate?.getTime() || 0;
        const dateB = b.ingressoDate?.getTime() || 0;
        return dateA - dateB;
      });
  }, [userTimbrature, userTimbratureQr]);

  const applyTimbraturaToForm = useCallback((timbratura) => {
    setHasTimbratura(!!timbratura);
    setRelatedTimbratura(timbratura);

    if (!timbratura) return;

    setFormData((prev) => ({
      ...prev,
      oraIngresso: formatTimeValue(timbratura.oraIngresso),
      oraUscita: formatTimeValue(timbratura.oraUscita),
      cantiere: timbratura.cantiere || prev.cantiere,
      mezzo: timbratura.mezzo || prev.mezzo,
      note: timbratura.note || prev.note,
    }));
  }, []);

  // Format date helper
  const formatDate = (timestamp) => {
    if (!timestamp) return "-";
    return format(timestamp.toDate(), "dd/MM/yyyy", { locale: it });
  };

  // Helper function to round entry time to next half hour
  const roundEntryTimeToNextHalfHour = (date) => {
    const rounded = new Date(date);
    const minutes = rounded.getMinutes();

    if (minutes === 0) {
      // Already at exact hour, no rounding needed
      return rounded;
    } else if (minutes <= 30) {
      // Round to next half hour (30 minutes)
      rounded.setMinutes(30, 0, 0);
    } else {
      // Round to next hour
      rounded.setHours(rounded.getHours() + 1, 0, 0, 0);
    }

    return rounded;
  };

  // Helper function to round exit time to previous half hour
  const roundExitTimeToPreviousHalfHour = (date) => {
    const rounded = new Date(date);
    const minutes = rounded.getMinutes();

    if (minutes === 0 || minutes === 30) {
      // Already at exact half hour, no rounding needed
      return rounded;
    } else if (minutes < 30) {
      // Round down to previous hour
      rounded.setMinutes(0, 0, 0);
    } else {
      // Round down to current half hour (30 minutes)
      rounded.setMinutes(30, 0, 0);
    }

    return rounded;
  };

  // Calculate total hours for presenze with rounding
  const calculateTotalHours = (presenze) => {
    return presenze.reduce((total, presenza) => {
      if (presenza.oraIngresso && presenza.oraUscita) {
        const { startDate: originalIngresso, endDate: originalUscita } =
          normalizeWorkDateRange(presenza.oraIngresso, presenza.oraUscita);

        if (!originalIngresso || !originalUscita) {
          return total;
        }

        // Apply rounding
        const ingressoRounded = roundEntryTimeToNextHalfHour(originalIngresso);
        const uscitaRounded = roundExitTimeToPreviousHalfHour(originalUscita);

        const diffMs = uscitaRounded - ingressoRounded;
        let diffHours = diffMs / (1000 * 60 * 60);

        // Subtract pause time if present
        if (presenza.durataPausa && presenza.durataPausa !== "nessuno") {
          const pauseHours = parseFloat(presenza.durataPausa);
          diffHours -= pauseHours;
        }

        // Ensure we don't have negative hours
        diffHours = Math.max(0, diffHours);

        return total + diffHours;
      }
      return total;
    }, 0);
  };

  const totalHoursPresenze = calculateTotalHours(userPresenze);

  // Helper function to sanitize text for PDF
  const sanitizeForPDF = (text) => {
    if (!text || typeof text !== "string") return "";
    // Remove potentially dangerous characters and limit length
    return text
      .replace(/[<>"'&]/g, "") // Remove HTML/XML chars
      .split("")
      .filter((char) => {
        const code = char.charCodeAt(0);
        return code >= 32 && code !== 127; // Keep printable characters only
      })
      .join("")
      .substring(0, 500) // Limit length
      .trim();
  };

  // PDF generation functions
  const generatePresenzePDFData = () => {
    if (!userPresenze) return [];

    // Return presenze data sorted by date with sanitized content
    return userPresenze.map((presenza) => ({
      ...presenza,
      dataRegistrazione: presenza.dataRegistrazione,
      oraIngresso: presenza.oraIngresso,
      oraUscita: presenza.oraUscita,
      cantiere: sanitizeForPDF(
        presenza.cantiere?.nome ||
          presenza.cantiere?.label ||
          presenza.cantiere ||
          "",
      ),
      note: sanitizeForPDF(presenza.note || ""),
      isFerie: presenza.isFerie,
      isMalattia: presenza.isMalattia,
      codiceMalattia: sanitizeForPDF(presenza.codiceMalattia || ""),
    }));
  };

  // Check for existing timbratura on selected date
  const checkForTimbratura = useCallback(
    (selectedDate, existingPresenza = null) => {
      if (!selectedDate) return;

      const matchedLegacy = userTimbrature
        .map((timbratura) => normalizeTimbratura(timbratura, "timbrature"))
        .filter((timbratura) => timbratura.dataRiferimento === selectedDate);

      const matchedQr = userTimbratureQr
        .map((timbratura) => normalizeTimbratura(timbratura, "timbrature_qr"))
        .filter((timbratura) => timbratura.dataRiferimento === selectedDate);

      const matchedTimbrature = [...matchedLegacy, ...matchedQr].sort(
        (a, b) => {
          const dateA = a.ingressoDate?.getTime() || 0;
          const dateB = b.ingressoDate?.getTime() || 0;
          return dateA - dateB;
        },
      );

      setAvailableTimbrature(matchedTimbrature);

      if (!matchedTimbrature.length) {
        setHasTimbratura(false);
        setRelatedTimbratura(null);
        return;
      }

      const preferredKey = existingPresenza?.timbraturaQrId
        ? `timbrature_qr:${existingPresenza.timbraturaQrId}`
        : existingPresenza?.timbraturaId
          ? `timbrature:${existingPresenza.timbraturaId}`
          : null;

      const preferredTimbratura = preferredKey
        ? matchedTimbrature.find(
            (timbratura) => timbratura.selectionKey === preferredKey,
          )
        : null;

      if (preferredTimbratura) {
        applyTimbraturaToForm(preferredTimbratura);
        return;
      }

      if (matchedTimbrature.length === 1) {
        applyTimbraturaToForm(matchedTimbrature[0]);
        return;
      }

      setHasTimbratura(false);
      setRelatedTimbratura(null);
    },
    [userTimbrature, userTimbratureQr, applyTimbraturaToForm],
  );

  const prefillFromAttivita = async (
    selectedDate,
    hasExistingPresenza = false,
  ) => {
    try {
      if (!selectedDate || hasExistingPresenza) {
        return;
      }
      const dateTs = timestamp.fromDate(new Date(`${selectedDate}T00:00:00`));
      const snap = await database
        .collection("attivita")
        .where("userId", "==", user.uid)
        .where("date", "==", dateTs)
        .get();
      if (snap.empty) {
        return;
      }
      const data = snap.docs[0].data();
      let projectName = "";
      if (data.project) {
        if (data.project.id && Array.isArray(projectsData)) {
          const proj = projectsData.find((p) => p.id === data.project.id);
          if (proj) {
            projectName = proj.nome || "";
          }
        } else if (data.project.nome) {
          projectName = data.project.nome;
        } else if (data.project.codice && Array.isArray(projectsData)) {
          const projByCode = projectsData.find(
            (p) => p.codice === data.project.codice,
          );
          if (projByCode) {
            projectName = projByCode.nome || "";
          }
        }
      }
      let mezzoObj = null;
      if (data.mezzo?.id && Array.isArray(automezziData)) {
        mezzoObj = automezziData.find((m) => m.id === data.mezzo.id) || null;
      }
      setFormData((prev) => ({
        ...prev,
        cantiere: projectName || prev.cantiere,
        mezzo: mezzoObj || prev.mezzo,
      }));
    } catch {
      // Ignore error
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "dataRegistrazione") {
      setFormData((prev) => ({ ...prev, [name]: value }));
      const existingPresenza = userPresenze.find((presenza) => {
        const presenzaDate = presenza.dataRegistrazione?.toDate();
        return presenzaDate && format(presenzaDate, "yyyy-MM-dd") === value;
      });
      checkForTimbratura(value, existingPresenza);
      prefillFromAttivita(value, !!existingPresenza);
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  // Handle React-Select changes
  const handleSelectChange = (selectedOption, actionMeta) => {
    const { name } = actionMeta;
    setFormData((prev) => ({
      ...prev,
      [name]: selectedOption ? selectedOption.value : "",
    }));
  };

  const handleTimbraturaSelection = (selectionKey) => {
    const timbratura = availableTimbrature.find(
      (item) => item.selectionKey === selectionKey,
    );

    if (!timbratura) {
      setHasTimbratura(false);
      setRelatedTimbratura(null);
      return;
    }

    applyTimbraturaToForm(timbratura);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setHasTimbratura(false);
    setRelatedTimbratura(null);
    setAvailableTimbrature([]);
  };

  useEffect(() => {
    if (!showAddModal || !formData.dataRegistrazione) return;

    const existingPresenza = userPresenze.find((presenza) => {
      const presenzaDate = presenza.dataRegistrazione?.toDate();
      return (
        presenzaDate &&
        format(presenzaDate, "yyyy-MM-dd") === formData.dataRegistrazione
      );
    });

    checkForTimbratura(formData.dataRegistrazione, existingPresenza);
  }, [
    showAddModal,
    formData.dataRegistrazione,
    userPresenze,
    checkForTimbratura,
  ]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation based on presence type
    if (formData.tipoPresenza === "lavorativa") {
      // For work presence, notes field is mandatory if:
      // 1. No timbratura is linked, OR
      // 2. Timbratura is linked but missing exit time
      const isMissingExitTime =
        hasTimbratura && relatedTimbratura && !relatedTimbratura.oraUscita;
      if ((!hasTimbratura || isMissingExitTime) && !formData.note.trim()) {
        const reason = !hasTimbratura
          ? "quando non è presente una timbratura collegata"
          : "quando nella timbratura collegata manca l'orario di uscita";
        alert(`Il campo note è obbligatorio ${reason}.`);
        return;
      }
    } else if (formData.tipoPresenza === "malattia") {
      // For sick leave, protocol code is mandatory
      if (!formData.codiceMalattia.trim()) {
        alert("Il protocollo di malattia è obbligatorio.");
        return;
      }
    }
    // For holidays (ferie) and rest days (riposo), no additional validation needed

    await savePresenza();
  };

  // Save presenza to Firestore
  const savePresenza = async () => {
    try {
      const buildPresenceDateTime = (timeValue, fallbackTimestamp = null) => {
        if (!timeValue) return null;
        const fallbackDate = getDateFromValue(fallbackTimestamp);
        const datePart = fallbackDate
          ? format(fallbackDate, "yyyy-MM-dd")
          : formData.dataRegistrazione;

        return new Date(`${datePart}T${timeValue}:00`);
      };

      const presenzaData = {
        userId: user.uid,
        userEmail: user.email,
        dataRegistrazione: new Date(formData.dataRegistrazione + "T00:00:00"),
        tipoPresenza: formData.tipoPresenza,
        // Work-related fields (only for lavorativa)
        oraIngresso:
          formData.tipoPresenza === "lavorativa" && formData.oraIngresso
            ? buildPresenceDateTime(
                formData.oraIngresso,
                relatedTimbratura?.oraIngresso,
              )
            : null,
        oraUscita:
          formData.tipoPresenza === "lavorativa" && formData.oraUscita
            ? buildPresenceDateTime(
                formData.oraUscita,
                relatedTimbratura?.oraUscita,
              )
            : null,
        cantiere:
          formData.tipoPresenza === "lavorativa" && formData.cantiere
            ? {
                nome: formData.cantiere,
                codice:
                  projectsData?.find((p) => p.nome === formData.cantiere)
                    ?.codice || null,
              }
            : null,
        mezzo:
          formData.tipoPresenza === "lavorativa" &&
          formData.mezzo &&
          formData.mezzo !== "nessuno"
            ? {
                id: formData.mezzo.id,
                modello: formData.mezzo.modello,
                targa: formData.mezzo.targa,
              }
            : null,
        // Pause duration (only for lavorativa)
        durataPausa:
          formData.tipoPresenza === "lavorativa"
            ? formData.durataPausa === "nessuno"
              ? "nessuno"
              : parseFloat(formData.durataPausa) // Save directly in hours
            : null,
        // Legacy fields for backward compatibility
        isFerie: formData.tipoPresenza === "ferie",
        isMalattia: formData.tipoPresenza === "malattia",
        isRiposo: formData.tipoPresenza === "riposo",
        // Sick leave protocol (only for malattia)
        codiceMalattia:
          formData.tipoPresenza === "malattia" ? formData.codiceMalattia : null,
        note: formData.note,
        hasTimbratura:
          formData.tipoPresenza === "lavorativa" ? !!relatedTimbratura : false,
        timbraturaId:
          formData.tipoPresenza === "lavorativa" &&
          relatedTimbratura?.sourceType === "timbrature"
            ? relatedTimbratura.id
            : null,
        timbraturaQrId:
          formData.tipoPresenza === "lavorativa" &&
          relatedTimbratura?.sourceType === "timbrature_qr"
            ? relatedTimbratura.id
            : null,
        createdAt: new Date(),
      };

      await addDocument(presenzaData);

      // Reset form and close modal
      setFormData({
        dataRegistrazione: "",
        tipoPresenza: "lavorativa",
        oraIngresso: "",
        oraUscita: "",
        cantiere: "",
        mezzo: "",
        durataPausa: "1",
        isFerie: false,
        isMalattia: false,
        isRiposo: false,
        codiceMalattia: "",
        note: "",
      });
      setShowAddModal(false);
      setHasTimbratura(false);
      setRelatedTimbratura(null);
      setAvailableTimbrature([]);
    } catch (error) {
      // Error logged for debugging purposes
    }
  };

  // Handle reperibilita click from mini calendar
  const handleReperibilitaClick = (rep) => {
    setSelectedReperibilita(rep);
    setDetailsInterventi(rep.interventi || []);
    setShowDetailsModal(true);
  };

  // Handle day click from mini calendar
  const handleDayClick = (day) => {
    const clickedDate = format(day, "yyyy-MM-dd");

    // Check if there's existing presence data for this day
    const existingPresenza = userPresenze.find((presenza) => {
      const presenzaDate = presenza.dataRegistrazione?.toDate();
      return presenzaDate && format(presenzaDate, "yyyy-MM-dd") === clickedDate;
    });

    // Pre-populate form with existing data or default values
    if (existingPresenza) {
      setFormData({
        dataRegistrazione: clickedDate,
        tipoPresenza: existingPresenza.tipoPresenza || "lavorativa",
        oraIngresso: formatTimeValue(existingPresenza.oraIngresso),
        oraUscita: formatTimeValue(existingPresenza.oraUscita),
        cantiere:
          existingPresenza.cantiere &&
          typeof existingPresenza.cantiere === "object"
            ? existingPresenza.cantiere.nome
            : existingPresenza.cantiere || "",
        mezzo:
          existingPresenza.mezzo && typeof existingPresenza.mezzo === "object"
            ? existingPresenza.mezzo
            : existingPresenza.mezzo || "",
        durataPausa: existingPresenza.durataPausa || "nessuno",
        isFerie: existingPresenza.isFerie || false,
        isMalattia: existingPresenza.isMalattia || false,
        isRiposo: existingPresenza.isRiposo || false,
        codiceMalattia: existingPresenza.codiceMalattia || "",
        note: existingPresenza.note || "",
      });
    } else {
      // Set default values for new presence
      setFormData({
        dataRegistrazione: clickedDate,
        tipoPresenza: "lavorativa",
        oraIngresso: "",
        oraUscita: "",
        cantiere: "",
        mezzo: "",
        durataPausa: "1",
        isFerie: false,
        isMalattia: false,
        isRiposo: false,
        codiceMalattia: "",
        note: "",
      });
    }

    // Check for related timbratura
    checkForTimbratura(clickedDate, existingPresenza);
    prefillFromAttivita(clickedDate, !!existingPresenza);

    // Open the modal
    setShowAddModal(true);
  };

  // Handle on-call form input changes
  const handleReperibilityInputChange = (e) => {
    const { name, value } = e.target;
    setReperibilityFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle on-call form submission
  const handleReperibilitySubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!reperibilityFormData.dataInizio || !reperibilityFormData.dataFine) {
      alert("Le date di inizio e fine sono obbligatorie.");
      return;
    }

    const startDate = new Date(reperibilityFormData.dataInizio);
    const endDate = new Date(reperibilityFormData.dataFine);

    if (startDate > endDate) {
      alert("La data di inizio non può essere successiva alla data di fine.");
      return;
    }

    try {
      const reperibilityData = {
        userId: user.uid,
        userEmail: user.email,
        dataInizio: new Date(reperibilityFormData.dataInizio + "T00:00:00"),
        dataFine: new Date(reperibilityFormData.dataFine + "T23:59:59"),
        note: reperibilityFormData.note,
        createdAt: new Date(),
      };

      await addReperibilita(reperibilityData);

      // Reset form and close modal
      setReperibilityFormData({
        dataInizio: "",
        dataFine: "",
        note: "",
        interventi: [],
      });
      setShowReperibilityModal(false);
    } catch (error) {
      alert("Errore durante il salvataggio della reperibilità.");
    }
  };

  // Handle Malattia form input changes
  const handleMalattiaInputChange = (e) => {
    const { name, value } = e.target;
    setMalattiaFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle Malattia form submission
  const handleMalattiaSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!malattiaFormData.dataInizio || !malattiaFormData.dataFine) {
      alert("Le date di inizio e fine sono obbligatorie.");
      return;
    }
    if (!malattiaFormData.note.trim()) {
      alert("Il campo note è obbligatorio per la malattia.");
      return;
    }

    const startDate = new Date(malattiaFormData.dataInizio);
    const endDate = new Date(malattiaFormData.dataFine);

    if (startDate > endDate) {
      alert("La data di inizio non può essere successiva alla data di fine.");
      return;
    }

    try {
      const days = eachDayOfInterval({ start: startDate, end: endDate });

      for (const day of days) {
        const dateStr = format(day, "yyyy-MM-dd");
        const dateTs = timestamp.fromDate(new Date(`${dateStr}T00:00:00`));

        // 1. Create Presenza (Malattia)
        const presenzaData = {
          userId: user.uid,
          userEmail: user.email,
          dataRegistrazione: new Date(`${dateStr}T00:00:00`),
          tipoPresenza: "malattia",
          oraIngresso: null,
          oraUscita: null,
          cantiere: null,
          mezzo: null,
          durataPausa: null,
          isFerie: false,
          isMalattia: true,
          isRiposo: false,
          codiceMalattia: malattiaFormData.note,
          note: "",
          hasTimbratura: false,
          createdAt: new Date(),
        };

        await addDocument(presenzaData);

        // 2. Create Attivita (Assenza)
        const docId = `${user.uid}_${dateStr}`;
        const attivitaPayload = {
          userId: user.uid,
          date: dateTs,
          project: null,
          mezzo: null,
          note: malattiaFormData.note,
          status: "assenza",
          createdBy: user.uid,
          updatedBy: user.uid,
          updatedAt: timestamp.fromDate(new Date()),
          createdAt: timestamp.fromDate(new Date()),
        };

        await database
          .collection("attivita")
          .doc(docId)
          .set(attivitaPayload, { merge: true });
      }

      // Reset form and close modal
      setMalattiaFormData({
        dataInizio: "",
        dataFine: "",
        note: "",
      });
      setShowMalattiaModal(false);
    } catch (error) {
      console.error("Errore salvataggio malattia:", error);
      alert("Errore durante il salvataggio della malattia.");
    }
  };

  if (!user) {
    return (
      <div className="foglio-presenza-container">
        <div className="access-denied">
          <h2>Accesso Richiesto</h2>
          <p>
            Devi essere autenticato per visualizzare il tuo foglio presenza.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="foglio-presenza-container">
      <div className="foglio-presenza-header">
        <h1>Il Mio Foglio Presenza</h1>
        <p>Visualizza le tue presenze e timbrature personali</p>
        <div className="user-info">
          <strong>Utente:</strong> {user.displayName || user.email}
        </div>
      </div>

      {/* Month Filter */}
      <div className="project-filter">
        <div className="filters-container">
          <div className="filter-group">
            <label>Filtra per Mese:</label>
            <input
              type="month"
              value={monthFilter}
              onChange={(e) => setMonthFilter(e.target.value)}
              className="search-bar"
            />
          </div>
        </div>
      </div>

      {/* Add Presence Button - Prominently placed */}
      <div className="add-presence-section">
        <button
          className="add-presence-btn primary-btn"
          onClick={() => setShowAddModal(true)}
        >
          + Compila FP
        </button>
        <button
          className="pdf-download-btn"
          onClick={() => {
            try {
              const periodo = {
                mese: new Date(monthFilter).getMonth(),
                anno: new Date(monthFilter).getFullYear(),
              };
              const userName = (user?.displayName || user?.email || "utente")
                .replace(/[^a-zA-Z0-9_-]/g, "_")
                .substring(0, 50);
              const dateStr = format(new Date(monthFilter), "MM-yyyy");
              const fileName = `presenze_${userName}_${dateStr}.pdf`;
              downloadPresenzeDipendentePdf({
                presenze: generatePresenzePDFData(),
                user,
                periodo,
                fileName,
                reperibilita: userReperibilita,
                projects: projectsData,
                isAdmin: userProfile?.ruolo === "admin",
              });
            } catch (e) {
              console.error("Errore nella generazione del PDF:", e);
            }
          }}
          title="Scarica il PDF delle presenze"
        >
          Scarica PDF Presenze
        </button>

        {userProfile?.canViewValorization && (
          <button
            className="pdf-download-btn"
            style={{ marginLeft: "10px", backgroundColor: "#17a2b8" }}
            onClick={() => setShowValorizationModal(true)}
          >
            Riepilogo Spettanze
          </button>
        )}

        <button
          className="reperibility-btn"
          onClick={() => setShowReperibilityModal(true)}
        >
          Inserisci Reperibilità
        </button>
        <button
          className="reperibility-btn"
          style={{ backgroundColor: "#dc3545", marginLeft: "10px" }}
          onClick={() => setShowMalattiaModal(true)}
        >
          Inserisci Malattia
        </button>
      </div>

      {/* Mini Calendar */}
      <MiniCalendario
        presenze={userPresenze}
        timbrature={allUserTimbrature}
        reperibilita={userReperibilita}
        monthFilter={monthFilter}
        onDayClick={handleDayClick}
        onReperibilitaClick={handleReperibilitaClick}
      />

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button
          className={`tab-button ${activeTab === "presenze" ? "active" : ""}`}
          onClick={() => setActiveTab("presenze")}
        >
          Le Mie Presenze ({userPresenze.length})
        </button>
        <button
          className={`tab-button ${activeTab === "timbrature" ? "active" : ""}`}
          onClick={() => setActiveTab("timbrature")}
        >
          Le Mie Timbrature ({allUserTimbrature.length})
        </button>
      </div>
      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <h3>Presenze</h3>
          <div className="summary-number">{userPresenze.length}</div>
          <div className="summary-detail">
            Totale ore: {totalHoursPresenze.toFixed(2)}h
          </div>
        </div>
        <div className="summary-card">
          <h3>Timbrature</h3>
          <div className="summary-number">{allUserTimbrature.length}</div>
        </div>
      </div>
      {/* Presenze Tab */}
      {activeTab === "presenze" && (
        <div className="tab-content">
          <div className="section-header">
            <h2>Le Mie Presenze</h2>
          </div>

          {/* Presenze Table */}
          <div className="table-container">
            {presenzeError && (
              <div className="error-message">
                Errore nel caricamento presenze: {presenzeError}
              </div>
            )}
            <table className="data-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Ingresso</th>
                  <th>Uscita</th>
                  <th>Ore Lavorate</th>
                  <th>Cantiere</th>
                  <th>Mezzo</th>
                  <th>Tipo</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {userPresenze.map((presenza) => {
                  let oreLavorate = "-";
                  if (presenza.oraIngresso && presenza.oraUscita) {
                    const {
                      startDate: originalIngresso,
                      endDate: originalUscita,
                    } = normalizeWorkDateRange(
                      presenza.oraIngresso,
                      presenza.oraUscita,
                    );

                    if (originalIngresso && originalUscita) {
                      // Apply rounding
                      const ingressoRounded =
                        roundEntryTimeToNextHalfHour(originalIngresso);
                      const uscitaRounded =
                        roundExitTimeToPreviousHalfHour(originalUscita);

                      const diffMs = uscitaRounded - ingressoRounded;
                      let diffHours = diffMs / (1000 * 60 * 60);

                      // Subtract pause time if present
                      if (
                        presenza.durataPausa &&
                        presenza.durataPausa !== "nessuno"
                      ) {
                        const pauseHours = parseFloat(presenza.durataPausa);
                        diffHours -= pauseHours;
                      }

                      // Ensure we don't have negative hours
                      diffHours = Math.max(0, diffHours);

                      oreLavorate = diffHours.toFixed(2) + "h";
                    }
                  }

                  return (
                    <tr key={presenza.id}>
                      <td>{formatDate(presenza.dataRegistrazione)}</td>
                      <td>{formatTime(presenza.oraIngresso)}</td>
                      <td>{formatTime(presenza.oraUscita)}</td>
                      <td>{oreLavorate}</td>
                      <td>{presenza.cantiere?.nome || "-"}</td>
                      <td>
                        {presenza.mezzo?.modello ||
                          presenza.mezzo?.targa ||
                          "-"}
                      </td>
                      <td>
                        <span
                          className={`tipo-badge ${
                            presenza.isFerie
                              ? "ferie"
                              : presenza.isMalattia
                                ? "malattia"
                                : presenza.isRiposo
                                  ? "riposo"
                                  : "lavoro"
                          }`}
                        >
                          {presenza.isFerie
                            ? "Ferie"
                            : presenza.isMalattia
                              ? "Malattia"
                              : presenza.isRiposo
                                ? "Riposo"
                                : "Lavoro"}
                        </span>
                      </td>
                      <td>{presenza.note || "-"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {userPresenze.length === 0 && (
              <div className="no-data">
                Nessuna presenza trovata per il periodo selezionato
              </div>
            )}
          </div>

          {/* On-call (Reperibilità) Section */}
          <div className="section-header">
            <h2>Le Mie Reperibilità</h2>
          </div>

          <div className="table-container">
            {reperibilityError && (
              <div className="error-message">
                Errore nel caricamento reperibilità: {reperibilityError}
              </div>
            )}
            <table className="data-table">
              <thead>
                <tr>
                  <th>Data Inizio</th>
                  <th>Data Fine</th>
                  <th>Durata (giorni)</th>
                  <th>Note</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {userReperibilita.map((rep) => {
                  const startDate = rep.dataInizio?.toDate();
                  const endDate = rep.dataFine?.toDate();
                  let duration = "-";

                  if (startDate && endDate) {
                    // Calculate days inclusive (same day = 1 day)
                    const startDay = new Date(
                      startDate.getFullYear(),
                      startDate.getMonth(),
                      startDate.getDate(),
                    );
                    const endDay = new Date(
                      endDate.getFullYear(),
                      endDate.getMonth(),
                      endDate.getDate(),
                    );
                    const diffTime = endDay - startDay;
                    const diffDays =
                      Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
                    duration = diffDays + " giorni";
                  }

                  return (
                    <tr key={rep.id}>
                      <td>{formatDate(rep.dataInizio)}</td>
                      <td>{formatDate(rep.dataFine)}</td>
                      <td>{duration}</td>
                      <td>{rep.note || "-"}</td>
                      <td>
                        <button
                          className="btn-details"
                          onClick={() => handleOpenDetails(rep)}
                          title="Gestisci interventi"
                          style={{
                            padding: "4px 8px",
                            fontSize: "0.85rem",
                            cursor: "pointer",
                            backgroundColor: "#17a2b8",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                          }}
                        >
                          Interventi ({rep.interventi?.length || 0})
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {userReperibilita.length === 0 && (
              <div className="no-data">
                Nessuna reperibilità trovata per il periodo selezionato
              </div>
            )}
          </div>
        </div>
      )}

      {/* Timbrature Tab */}
      {activeTab === "timbrature" && (
        <div className="tab-content">
          <div className="section-header">
            <h2>Le Mie Timbrature</h2>
          </div>

          {/* Timbrature Table */}
          <div className="table-container">
            {timbratureError && (
              <div className="error-message">
                Errore nel caricamento timbrature: {timbratureError}
              </div>
            )}
            {timbratureQrError && (
              <div className="error-message">
                Errore nel caricamento timbrature QR: {timbratureQrError}
              </div>
            )}
            <table className="data-table">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Data</th>
                  <th>Ingresso</th>
                  <th>Uscita</th>
                </tr>
              </thead>
              <tbody>
                {allUserTimbrature.map((timbratura) => {
                  return (
                    <tr key={timbratura.selectionKey}>
                      <td>{timbratura.sourceLabel}</td>
                      <td>{timbratura.dataRiferimento || "-"}</td>
                      <td>{formatTimeValue(timbratura.oraIngresso) || "-"}</td>
                      <td>{formatTimeValue(timbratura.oraUscita) || "-"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {allUserTimbrature.length === 0 && (
              <div className="no-data">
                Nessuna timbratura trovata per il periodo selezionato
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal for adding new presence */}
      {showAddModal && (
        <div className="modal-overlay" onClick={closeAddModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Aggiungi Nuova Presenza</h3>
              <button className="close-btn" onClick={closeAddModal}>
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="presence-form">
              <div className="form-group">
                <label htmlFor="dataRegistrazione">Data *</label>
                <input
                  type="date"
                  id="dataRegistrazione"
                  name="dataRegistrazione"
                  value={formData.dataRegistrazione}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="tipoPresenza">Tipo Presenza *</label>
                <Select
                  name="tipoPresenza"
                  value={tipoPresenzaOptions.find(
                    (option) => option.value === formData.tipoPresenza,
                  )}
                  onChange={handleSelectChange}
                  options={tipoPresenzaOptions}
                  placeholder="Seleziona tipo presenza..."
                  isSearchable={false}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  menuPortalTarget={portalTarget}
                />
              </div>

              {formData.tipoPresenza === "lavorativa" &&
                availableTimbrature.length > 0 && (
                  <div className="timbratura-info">
                    {availableTimbrature.length === 1 && relatedTimbratura ? (
                      <p className="info-message">
                        ✓ Trovata 1 timbratura per questa data. I dati sono
                        stati precompilati automaticamente.
                      </p>
                    ) : (
                      <>
                        <p className="info-message">
                          Trovate {availableTimbrature.length} timbrature per
                          questa data. Seleziona quella da associare alla
                          presenza.
                        </p>
                        <div className="timbratura-choice-list">
                          {availableTimbrature.map((timbratura) => (
                            <button
                              key={timbratura.selectionKey}
                              type="button"
                              className={`timbratura-choice ${
                                relatedTimbratura?.selectionKey ===
                                timbratura.selectionKey
                                  ? "active"
                                  : ""
                              }`}
                              onClick={() =>
                                handleTimbraturaSelection(
                                  timbratura.selectionKey,
                                )
                              }
                            >
                              <span className="timbratura-choice-header">
                                <span className="timbratura-source-badge">
                                  {timbratura.sourceLabel}
                                </span>
                                <strong>{timbratura.label}</strong>
                              </span>
                              <span className="timbratura-choice-meta">
                                Riferimento: {timbratura.dataRiferimento}
                              </span>
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}

              {/* Campi per presenza lavorativa */}
              {formData.tipoPresenza === "lavorativa" && (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="oraIngresso">Ora Ingresso</label>
                      <input
                        type="time"
                        id="oraIngresso"
                        name="oraIngresso"
                        value={formData.oraIngresso}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="oraUscita">Ora Uscita</label>
                      <input
                        type="time"
                        id="oraUscita"
                        name="oraUscita"
                        value={formData.oraUscita}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="durataPausa">Pausa</label>
                    <Select
                      id="durataPausa"
                      name="durataPausa"
                      value={{
                        value: formData.durataPausa,
                        label:
                          formData.durataPausa === "nessuno"
                            ? "Nessuna pausa"
                            : `${formData.durataPausa} ore`,
                      }}
                      onChange={handleSelectChange}
                      options={[
                        { value: "nessuno", label: "Nessuna pausa" },
                        { value: "1", label: "1 ora (60 min)" },
                        { value: "1.5", label: "1.5 ore (90 min)" },
                        { value: "2", label: "2 ore (120 min)" },
                      ]}
                      placeholder="Seleziona durata pausa"
                      isSearchable={false}
                      className="react-select-container"
                      classNamePrefix="react-select"
                      menuPortalTarget={portalTarget}
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="cantiere">Cantiere</label>
                      <Select
                        id="cantiere"
                        name="cantiere"
                        value={
                          cantiereOptions.find(
                            (option) => option.value === formData.cantiere,
                          ) || null
                        }
                        onChange={handleSelectChange}
                        options={cantiereOptions}
                        placeholder="Seleziona cantiere"
                        isSearchable={true}
                        isClearable={true}
                        className="react-select-container"
                        classNamePrefix="react-select"
                        menuPortalTarget={portalTarget}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="mezzo">Mezzo</label>
                      <Select
                        id="mezzo"
                        name="mezzo"
                        value={
                          mezzoOptions.find(
                            (option) => option.value === formData.mezzo,
                          ) || null
                        }
                        onChange={handleSelectChange}
                        options={mezzoOptions}
                        placeholder="Seleziona mezzo"
                        isSearchable={true}
                        isClearable={true}
                        className="react-select-container"
                        classNamePrefix="react-select"
                        menuPortalTarget={portalTarget}
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Campo per codice malattia */}
              {formData.tipoPresenza === "malattia" && (
                <div className="form-group">
                  <label htmlFor="codiceMalattia">Protocollo Malattia *</label>
                  <input
                    type="text"
                    id="codiceMalattia"
                    name="codiceMalattia"
                    value={formData.codiceMalattia}
                    onChange={handleInputChange}
                    placeholder="Inserisci il protocollo di malattia..."
                    required
                  />
                </div>
              )}

              {/* Campo note - sempre visibile ma con logiche diverse */}
              <div className="form-group">
                <label htmlFor="note">
                  Note{" "}
                  {formData.tipoPresenza === "lavorativa" &&
                    (!hasTimbratura ||
                      (hasTimbratura &&
                        relatedTimbratura &&
                        !relatedTimbratura.oraUscita)) && (
                      <span className="required-asterisk">*</span>
                    )}
                </label>
                <textarea
                  id="note"
                  name="note"
                  value={formData.note}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder={
                    formData.tipoPresenza === "lavorativa" &&
                    (!hasTimbratura ||
                      (hasTimbratura &&
                        relatedTimbratura &&
                        !relatedTimbratura.oraUscita))
                      ? !hasTimbratura
                        ? "Note obbligatorie (nessuna timbratura collegata)..."
                        : "Note obbligatorie (manca orario di uscita)..."
                      : "Note aggiuntive..."
                  }
                  className={
                    formData.tipoPresenza === "lavorativa" &&
                    (!hasTimbratura ||
                      (hasTimbratura &&
                        relatedTimbratura &&
                        !relatedTimbratura.oraUscita)) &&
                    !formData.note.trim()
                      ? "required-field"
                      : ""
                  }
                />
                {formData.tipoPresenza === "lavorativa" &&
                  (!hasTimbratura ||
                    (hasTimbratura &&
                      relatedTimbratura &&
                      !relatedTimbratura.oraUscita)) && (
                    <small className="field-help">
                      * Campo obbligatorio{" "}
                      {!hasTimbratura
                        ? "quando non è presente una timbratura collegata"
                        : "quando nella timbratura collegata manca l'orario di uscita"}
                    </small>
                  )}
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeAddModal}
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={!formData.dataRegistrazione}
                >
                  {formData.tipoPresenza === "lavorativa" && "Salva Presenza"}
                  {formData.tipoPresenza === "ferie" && "Salva Ferie"}
                  {formData.tipoPresenza === "malattia" && "Salva Malattia"}
                  {formData.tipoPresenza === "riposo" && "Salva Riposo"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal for adding new on-call period */}
      {showReperibilityModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowReperibilityModal(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Aggiungi Periodo di Reperibilità</h3>
              <button
                className="close-btn"
                onClick={() => setShowReperibilityModal(false)}
              >
                ×
              </button>
            </div>

            <form
              onSubmit={handleReperibilitySubmit}
              className="reperibility-form"
            >
              <div className="form-group">
                <label htmlFor="dataInizio">Data Inizio *</label>
                <input
                  type="date"
                  id="dataInizio"
                  name="dataInizio"
                  value={reperibilityFormData.dataInizio}
                  onChange={handleReperibilityInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="dataFine">Data Fine *</label>
                <input
                  type="date"
                  id="dataFine"
                  name="dataFine"
                  value={reperibilityFormData.dataFine}
                  onChange={handleReperibilityInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="note">Note</label>
                <textarea
                  id="note"
                  name="note"
                  value={reperibilityFormData.note}
                  onChange={handleReperibilityInputChange}
                  rows="3"
                  placeholder="Note aggiuntive sulla reperibilità..."
                />
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowReperibilityModal(false)}
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={
                    !reperibilityFormData.dataInizio ||
                    !reperibilityFormData.dataFine
                  }
                >
                  Salva Reperibilità
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Modal for adding Malattia */}
      {showMalattiaModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowMalattiaModal(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Inserisci Malattia</h3>
              <button
                className="close-btn"
                onClick={() => setShowMalattiaModal(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleMalattiaSubmit} className="reperibility-form">
              <div className="form-group">
                <label htmlFor="malattiaDataInizio">Data Inizio *</label>
                <input
                  type="date"
                  id="malattiaDataInizio"
                  name="dataInizio"
                  value={malattiaFormData.dataInizio}
                  onChange={handleMalattiaInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="malattiaDataFine">Data Fine *</label>
                <input
                  type="date"
                  id="malattiaDataFine"
                  name="dataFine"
                  value={malattiaFormData.dataFine}
                  onChange={handleMalattiaInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="malattiaNote">Note (Obbligatorio) *</label>
                <textarea
                  id="malattiaNote"
                  name="note"
                  value={malattiaFormData.note}
                  onChange={handleMalattiaInputChange}
                  rows="3"
                  placeholder="Inserisci note o protocollo malattia..."
                  required
                />
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowMalattiaModal(false)}
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  style={{ backgroundColor: "#dc3545" }}
                  disabled={
                    !malattiaFormData.dataInizio ||
                    !malattiaFormData.dataFine ||
                    !malattiaFormData.note.trim()
                  }
                >
                  Salva Malattia
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Modal for managing interventions (Details) */}
      {showDetailsModal && selectedReperibilita && (
        <div
          className="modal-overlay"
          onClick={() => setShowDetailsModal(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Gestione Interventi Reperibilità</h3>
              <button
                className="close-btn"
                onClick={() => setShowDetailsModal(false)}
              >
                ×
              </button>
            </div>

            <div className="reperibility-form">
              <div
                className="reperibility-details-info"
                style={{
                  marginBottom: "20px",
                  padding: "10px",
                  backgroundColor: "#f0f0f0",
                  borderRadius: "4px",
                }}
              >
                <p>
                  <strong>Periodo:</strong>{" "}
                  {formatDate(selectedReperibilita.dataInizio)} -{" "}
                  {formatDate(selectedReperibilita.dataFine)}
                </p>
                <p>
                  <strong>Note:</strong> {selectedReperibilita.note || "-"}
                </p>
              </div>

              <div className="interventi-section">
                <h4>Nuovo Intervento</h4>
                <div className="intervento-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="interventoData">Data</label>
                      <input
                        type="date"
                        id="interventoData"
                        name="data"
                        value={currentIntervento.data}
                        onChange={handleInterventoChange}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="interventoLuogo">Luogo</label>
                      <input
                        type="text"
                        id="interventoLuogo"
                        name="luogo"
                        value={currentIntervento.luogo}
                        onChange={handleInterventoChange}
                        placeholder="Luogo intervento"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="interventoOraInizio">Ora Inizio</label>
                      <input
                        type="time"
                        id="interventoOraInizio"
                        name="oraInizio"
                        value={currentIntervento.oraInizio}
                        onChange={handleInterventoChange}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="interventoOraFine">Ora Fine</label>
                      <input
                        type="time"
                        id="interventoOraFine"
                        name="oraFine"
                        value={currentIntervento.oraFine}
                        onChange={handleInterventoChange}
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn-add-intervento"
                    onClick={addIntervento}
                  >
                    + Aggiungi alla lista
                  </button>
                </div>

                {/* List of interventions */}
                <div className="interventi-list" style={{ marginTop: "20px" }}>
                  <h5>
                    Interventi salvati/in lista ({detailsInterventi.length}):
                  </h5>
                  {detailsInterventi.length === 0 ? (
                    <p>Nessun intervento inserito.</p>
                  ) : (
                    <ul>
                      {detailsInterventi.map((int, index) => (
                        <li key={index} className="intervento-item">
                          <div className="intervento-details">
                            <strong>
                              {int.data
                                ? format(new Date(int.data), "dd/MM/yyyy", {
                                    locale: it,
                                  })
                                : "-"}
                            </strong>{" "}
                            - {int.oraInizio} / {int.oraFine}
                            <br />
                            <span className="intervento-luogo">
                              {int.luogo}
                            </span>
                          </div>
                          <button
                            type="button"
                            className="btn-remove-intervento"
                            onClick={() => removeIntervento(index)}
                            title="Rimuovi intervento"
                          >
                            ×
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <div
                className="form-actions"
                style={{
                  marginTop: "20px",
                  borderTop: "1px solid #ddd",
                  paddingTop: "15px",
                }}
              >
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowDetailsModal(false)}
                >
                  Annulla
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleSaveDetails}
                >
                  Salva Modifiche
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <ValorizationModal
        show={showValorizationModal}
        onClose={() => setShowValorizationModal(false)}
        presenze={userPresenze}
        reperibilita={userReperibilita}
        user={userProfile}
        monthFilter={monthFilter}
        documenti={documentiData}
        spese={speseData}
      />
    </div>
  );
}
